// NavigationContext.js - 분석 경로 자동 기록 시스템
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * Navigation Context
 * 
 * 사용자의 분석 경로를 자동으로 기록하고 관리
 * - 언제든 이전 단계로 복귀 가능
 * - 분석 경로 저장 및 공유
 * - 실험 장려 (언제든 되돌릴 수 있다는 안심감)
 * 
 * 벤치마킹 인사이트:
 * - Palantir: "분석 경로 추적"
 * - Git의 commit history 방식
 * - "실험해도 돌아올 수 있다" = 더 깊은 분석
 */

const NavigationContext = createContext();

export const NavigationProvider = ({ children }) => {
  // 네비게이션 스택
  const [navigationStack, setNavigationStack] = useState([
    { 
      label: '고객 360°', 
      path: '/customers', 
      filters: {},
      timestamp: Date.now()
    }
  ]);
  
  // 저장된 북마크 목록
  const [bookmarks, setBookmarks] = useState([]);
  
  // LocalStorage에서 북마크 로드
  useEffect(() => {
    const saved = localStorage.getItem('analysis_bookmarks');
    if (saved) {
      try {
        setBookmarks(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load bookmarks:', e);
      }
    }
  }, []);
  
  /**
   * 새로운 단계 추가
   */
  const pushNavigation = useCallback((label, path, filters = {}, metadata = {}) => {
    setNavigationStack(prev => [
      ...prev, 
      { 
        label, 
        path, 
        filters, 
        metadata,
        timestamp: Date.now() 
      }
    ]);
  }, []);
  
  /**
   * 특정 인덱스로 복귀 (이후 단계는 제거)
   */
  const popNavigation = useCallback((index) => {
    if (index >= 0 && index < navigationStack.length) {
      setNavigationStack(prev => prev.slice(0, index + 1));
      return navigationStack[index];
    }
  }, [navigationStack]);
  
  /**
   * 뒤로 가기 (1단계)
   */
  const goBack = useCallback(() => {
    if (navigationStack.length > 1) {
      const newStack = navigationStack.slice(0, -1);
      setNavigationStack(newStack);
      return newStack[newStack.length - 1];
    }
    return null;
  }, [navigationStack]);
  
  /**
   * 앞으로 가기 (실제로는 히스토리에서 복원)
   */
  const canGoBack = navigationStack.length > 1;
  
  /**
   * 현재 위치 가져오기
   */
  const getCurrentPath = useCallback(() => {
    return navigationStack[navigationStack.length - 1];
  }, [navigationStack]);
  
  /**
   * 전체 경로 가져오기 (스택 복사본)
   */
  const getFullPath = useCallback(() => {
    return [...navigationStack];
  }, [navigationStack]);
  
  /**
   * 북마크 저장
   */
  const saveBookmark = useCallback((customName = null) => {
    const bookmark = {
      id: `bookmark_${Date.now()}`,
      name: customName || `분석_${new Date().toLocaleDateString('ko-KR')}`,
      path: [...navigationStack],
      created_at: new Date().toISOString(),
      last_used: new Date().toISOString()
    };
    
    const newBookmarks = [...bookmarks, bookmark];
    setBookmarks(newBookmarks);
    
    // LocalStorage에 저장
    try {
      localStorage.setItem('analysis_bookmarks', JSON.stringify(newBookmarks));
    } catch (e) {
      console.error('Failed to save bookmark:', e);
    }
    
    return bookmark;
  }, [navigationStack, bookmarks]);
  
  /**
   * 북마크 로드
   */
  const loadBookmark = useCallback((bookmarkId) => {
    const bookmark = bookmarks.find(b => b.id === bookmarkId);
    if (bookmark) {
      setNavigationStack(bookmark.path);
      
      // 마지막 사용 시간 업데이트
      const updatedBookmarks = bookmarks.map(b =>
        b.id === bookmarkId 
          ? { ...b, last_used: new Date().toISOString() }
          : b
      );
      setBookmarks(updatedBookmarks);
      localStorage.setItem('analysis_bookmarks', JSON.stringify(updatedBookmarks));
      
      return bookmark.path[bookmark.path.length - 1];
    }
    return null;
  }, [bookmarks]);
  
  /**
   * 북마크 삭제
   */
  const deleteBookmark = useCallback((bookmarkId) => {
    const newBookmarks = bookmarks.filter(b => b.id !== bookmarkId);
    setBookmarks(newBookmarks);
    localStorage.setItem('analysis_bookmarks', JSON.stringify(newBookmarks));
  }, [bookmarks]);
  
  /**
   * 북마크 이름 변경
   */
  const renameBookmark = useCallback((bookmarkId, newName) => {
    const updatedBookmarks = bookmarks.map(b =>
      b.id === bookmarkId ? { ...b, name: newName } : b
    );
    setBookmarks(updatedBookmarks);
    localStorage.setItem('analysis_bookmarks', JSON.stringify(updatedBookmarks));
  }, [bookmarks]);
  
  /**
   * 경로 공유 (URL 생성)
   */
  const shareCurrentPath = useCallback(() => {
    const pathData = btoa(JSON.stringify(navigationStack));
    const shareUrl = `${window.location.origin}${window.location.pathname}?path=${pathData}`;
    
    // 클립보드에 복사
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => console.log('분석 경로가 클립보드에 복사되었습니다'))
        .catch(err => console.error('복사 실패:', err));
    }
    
    return shareUrl;
  }, [navigationStack]);
  
  /**
   * 공유된 경로 로드 (URL 파라미터에서)
   */
  const loadSharedPath = useCallback((pathData) => {
    try {
      const decoded = JSON.parse(atob(pathData));
      if (Array.isArray(decoded) && decoded.length > 0) {
        setNavigationStack(decoded);
        return true;
      }
    } catch (e) {
      console.error('Invalid path data:', e);
    }
    return false;
  }, []);
  
  // URL 파라미터에서 공유된 경로 자동 로드
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathData = params.get('path');
    if (pathData) {
      loadSharedPath(pathData);
    }
  }, [loadSharedPath]);
  
  const value = {
    // State
    navigationStack,
    bookmarks,
    canGoBack,
    
    // Navigation
    pushNavigation,
    popNavigation,
    goBack,
    getCurrentPath,
    getFullPath,
    
    // Bookmarks
    saveBookmark,
    loadBookmark,
    deleteBookmark,
    renameBookmark,
    
    // Sharing
    shareCurrentPath,
    loadSharedPath
  };
  
  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Navigation Hook
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

/**
 * 사용 예시:
 * 
 * // App.js에서 Provider 설정
 * <NavigationProvider>
 *   <App />
 * </NavigationProvider>
 * 
 * // 컴포넌트에서 사용
 * function DataAnalysis() {
 *   const { pushNavigation, goBack, saveBookmark } = useNavigation();
 *   
 *   const handleFilterChange = (regionName) => {
 *     pushNavigation(
 *       `${regionName} 지역`,
 *       '/analysis/region',
 *       { region: regionName }
 *     );
 *     // 실제 필터 적용 로직...
 *   };
 *   
 *   return (
 *     <div>
 *       <BreadcrumbTrail />
 *       <Button onClick={goBack}>뒤로</Button>
 *       <Button onClick={() => saveBookmark('주간 리뷰')}>북마크</Button>
 *     </div>
 *   );
 * }
 */

/**
 * 실전 활용 시나리오:
 * 
 * 1. 드릴다운 분석:
 *    전체 매출 → 지역별 → 서울 → 제품 A → 시계열 분석
 *    각 단계마다 pushNavigation() 호출
 * 
 * 2. 북마크:
 *    자주 보는 분석 경로를 저장
 *    "주간 매출 리뷰", "재고 현황 체크" 등
 * 
 * 3. 협업:
 *    shareCurrentPath()로 동료에게 분석 경로 공유
 *    "내가 발견한 패턴을 이 경로로 확인해보세요"
 * 
 * 4. 실험:
 *    여러 필터 조합을 시도해도 언제든 goBack()으로 복귀
 *    실수에 대한 두려움 제거
 */

